import { useQuery } from "@tanstack/react-query";
import { requestsRejectedApi } from "./axios";
import { DataI, ServiceResponseI, PropsRequestsRejectedI, RequestsRejectedI } from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getRequestsRejected = async ({
  pagination,
  sorting,
  globalFilter,
  equipmentId,
  client,
}: PropsRequestsRejectedI): Promise<ServiceResponseI<DataI<RequestsRejectedI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    
    if (equipmentId) {
      params.append("equipmentId", equipmentId.toString());
    }
    
    if (client) {
      params.append("client", client.toString());
    }

    const { data } = await requestsRejectedApi.get<ServiceResponseI<DataI<RequestsRejectedI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    throw new Error((err as Error).message);
  }
};

export const getAllRequestsRejected = async (): Promise<ServiceResponseI<RequestsRejectedI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await requestsRejectedApi.get<ServiceResponseI<RequestsRejectedI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((err as Error).message);
  }
};

export const getRequestsRejectedByEquipmentId = async (
  equipmentId: number
): Promise<ServiceResponseI<RequestsRejectedI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await requestsRejectedApi.get<ServiceResponseI<RequestsRejectedI[]>>(
      `/equipment/${equipmentId}`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((err as Error).message);
  }
};

export const getRequestsRejectedByDate = async (
  date: string
): Promise<ServiceResponseI<RequestsRejectedI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("date", date);

    const { data } = await requestsRejectedApi.get<ServiceResponseI<RequestsRejectedI[]>>(
      `/by-date`,
      { 
        params,
        headers: { Authorization: validateToken } 
      }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((err as Error).message);
  }
};

export const requestsRejectedPost = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await requestsRejectedApi.post<ServiceResponseI<string>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const requestsRejectedPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await requestsRejectedApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const requestsRejectedById = async (
  id: number
): Promise<ServiceResponseI<RequestsRejectedI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await requestsRejectedApi.get<ServiceResponseI<RequestsRejectedI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const requestsRejectedDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await requestsRejectedApi.delete<ServiceResponseI<string>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useRequestsRejected = ({ 
  sorting, 
  pagination, 
  globalFilter, 
  equipmentId,
  client 
}: PropsRequestsRejectedI) => {
  const requestsRejectedQuery = useQuery(
    ["requestsRejected", { pagination, sorting, globalFilter, equipmentId, client }],
    () => getRequestsRejected({ pagination, sorting, globalFilter, equipmentId, client }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    requestsRejectedQuery,
  };
};